import React, { useEffect, useState, useCallback } from "react";
import { CloudOff, RefreshCw, CheckCircle2, WifiOff } from "lucide-react";
import { count as queueCount, subscribe, flush } from "@/lib/offlineQueue";
import { api } from "@/lib/api";

/**
 * Tiny pill that lives in the dashboard topbar. Three states:
 *   1) Online + empty queue  → hidden
 *   2) Offline               → red pill "Offline — work continues, will sync later"
 *   3) Online + pending queue → amber pill with count + "Sync now" button
 *
 * Crucial for SXM employees on a roof — they always know whether their last
 * upload made it or whether it's still waiting in the queue.
 */
export default function OfflineStatusPill() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    queueCount().then(setPending);
    const unsub = subscribe(setPending);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      unsub();
    };
  }, []);

  const syncNow = useCallback(async () => {
    setSyncing(true);
    const { ok } = await flush(api);
    setSyncing(false);
    if (ok > 0) {
      setJustSynced(true);
      setTimeout(() => setJustSynced(false), 3500);
    }
  }, []);

  // Auto-trigger sync once we transition from offline → online with pending items
  useEffect(() => { if (online && pending > 0) syncNow(); }, [online, pending, syncNow]); // eslint-disable-line

  if (online && pending === 0 && !justSynced) return null;

  if (!online) {
    return (
      <div data-testid="offline-pill"
           className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40">
        <WifiOff className="w-3 h-3" />
        Offline · work saves locally
      </div>
    );
  }

  if (justSynced) {
    return (
      <div data-testid="synced-pill"
           className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/40">
        <CheckCircle2 className="w-3 h-3" /> Synced
      </div>
    );
  }

  return (
    <button onClick={syncNow} disabled={syncing} data-testid="queue-pill"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800/40 hover:bg-amber-200 dark:hover:bg-amber-900/40 transition">
      <CloudOff className="w-3 h-3" />
      {syncing ? <span className="inline-flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Syncing…</span> : <>{pending} pending · Sync now</>}
    </button>
  );
}
